import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  Paper,
  IconButton,
  Tooltip,
} from '@mui/material'
import { DataGrid } from '@mui/x-data-grid'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material'
import { interventionsAPI } from '../services/api'

const columns = [
  { field: 'id', headerName: 'ID', width: 70 },
  {
    field: 'content',
    headerName: 'Content',
    width: 300,
    renderCell: (params) => (
      <Box sx={{
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      }}>
        {params.value.substring(0, 100)}...
      </Box>
    )
  },
  {
    field: 'diagnosis_code',
    headerName: 'Diagnosis Codes',
    width: 200,
    valueGetter: (value, row, column, apiRef) => {
      return value.row.links
        .filter(link => link.diagnosis_code)
        .map(link => link.diagnosis_code)
        .filter((desc, index, arr) => arr.indexOf(desc) === index); // Remove duplicates
    },
    renderCell: (params) => {
      const diagnosisCodes = params.value || [];
      return (
        <Box>
          {diagnosisCodes.map((code, index) => (
            <Chip
              key={index}
              label={code}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ mr: 0.5, mb: 0.5 }}
            />
          ))}
        </Box>
      );
    }
  },
  {
    field: 'treatment_code',
    headerName: 'Treatment Codes',
    width: 200,
    valueGetter: (value, row, column, apiRef) => {
      return value.row.links
        .filter(link => link.treatment_code)
        .map(link => link.treatment_code)
        .filter((code, index, arr) => arr.indexOf(code) === index); // Remove duplicates
    },
    renderCell: (params) => {
      const treatmentCodes = params.value || [];
      return (
        <Box>
          {treatmentCodes.map((code, index) => (
            <Chip
              key={index}
              label={code}
              size="small"
              color="secondary"
              variant="outlined"
              sx={{ mr: 0.5, mb: 0.5 }}
            />
          ))}
        </Box>
      );
    }
  },

  {
    field: 'created_at',
    headerName: 'Created',
    width: 150,
    valueGetter: (params) => new Date(params.value).toLocaleDateString()
  },
]

function Interventions() {
  const [interventions, setInterventions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [openDialog, setOpenDialog] = useState(false)
  const [viewDialog, setViewDialog] = useState(false)
  const [selectedIntervention, setSelectedIntervention] = useState(null)
  const [selectedInterventionDays, setSelectedInterventionDays] = useState([])
  const [loadingDays, setLoadingDays] = useState(false)
  const [dayEditDialog, setDayEditDialog] = useState(false)
  const [selectedDay, setSelectedDay] = useState(null)
  const [formData, setFormData] = useState({
    content: '',
  })
  const [dayFormData, setDayFormData] = useState({
    content: '',
    priority: 1,
  })

  useEffect(() => {
    fetchInterventions()
  }, [])

  const fetchInterventions = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await interventionsAPI.getInterventionsWithLinks()
      setInterventions(response.data)
    } catch (err) {
      console.error('Error fetching interventions:', err)
      setError('Failed to load interventions. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const fetchInterventionDays = async (interventionId) => {
    try {
      setLoadingDays(true)
      const response = await interventionsAPI.getInterventionDays(interventionId)
      // Add unique IDs for MUI DataGrid
      const daysWithIds = response.data.map(day => ({
        ...day,
        id: `${interventionId}-${day.day}`
      }))
      setSelectedInterventionDays(daysWithIds)
    } catch (err) {
      console.error('Error fetching intervention days:', err)
      setSelectedInterventionDays([])
    } finally {
      setLoadingDays(false)
    }
  }

  const handleRowSelection = (intervention) => {
    if (intervention) {
      setSelectedIntervention(intervention)
      fetchInterventionDays(intervention.id)
    } else {
      setSelectedIntervention(null)
      setSelectedInterventionDays([])
    }
  }

  const handleDayEdit = (day) => {
    setSelectedDay(day)
    setDayFormData({
      content: day.content,
      priority: day.priority,
    })
    setDayEditDialog(true)
  }

  const handleDayEditClose = () => {
    setDayEditDialog(false)
    setSelectedDay(null)
    setDayFormData({
      content: '',
      priority: 1,
    })
  }

  const handleDayEditSubmit = async () => {
    try {
      await interventionsAPI.updateInterventionDay(
        selectedIntervention.id,
        selectedDay.day,
        dayFormData
      )
      handleDayEditClose()
      // Refresh the days data
      fetchInterventionDays(selectedIntervention.id)
    } catch (err) {
      console.error('Error updating intervention day:', err)
      setError('Failed to update intervention day. Please try again.')
    }
  }

  const handleOpenDialog = (intervention = null) => {
    if (intervention) {
      setSelectedIntervention(intervention)
      setFormData({
        content: intervention.content,
      })
    } else {
      setSelectedIntervention(null)
      setFormData({
        content: '',
      })
    }
    setOpenDialog(true)
  }

  const handleViewDialog = (intervention) => {
    setSelectedIntervention(intervention)
    setViewDialog(true)
  }

  const handleCloseDialog = () => {
    setOpenDialog(false)
    setViewDialog(false)
    setSelectedIntervention(null)
    setFormData({
      content: '',
    })
  }

  const handleSubmit = async () => {
    try {
      if (selectedIntervention) {
        await interventionsAPI.updateIntervention(selectedIntervention.id, formData)
      } else {
        await interventionsAPI.createIntervention(formData)
      }

      handleCloseDialog()
      fetchInterventions()
    } catch (err) {
      console.error('Error saving intervention:', err)
      setError('Failed to save intervention. Please try again.')
    }
  }

  const handleDelete = async (interventionId) => {
    if (window.confirm('Are you sure you want to delete this intervention?')) {
      try {
        await interventionsAPI.deleteIntervention(interventionId)
        fetchInterventions()
      } catch (err) {
        console.error('Error deleting intervention:', err)
        setError('Failed to delete intervention. Please try again.')
      }
    }
  }

  const actionColumn = {
    field: 'actions',
    headerName: '',
    width: 120,
    sortable: false,
    renderCell: (params) => (
      <Box display="flex" gap={0.5}>
        <Tooltip title="View">
          <IconButton size="small" onClick={() => handleViewDialog(params.row)}>
            <ViewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Edit">
          <IconButton size="small" onClick={() => handleOpenDialog(params.row)}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton size="small" color="error" onClick={() => handleDelete(params.row.id)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    ),
  }

  const allColumns = [...columns, actionColumn]

  const daysColumns = [
    { field: 'day', headerName: 'Day', width: 80 },
    {
      field: 'content',
      headerName: 'Content',
      width: 400,
      renderCell: (params) => (
        <Box sx={{
          maxWidth: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          {params.value}
        </Box>
      )
    },
    { field: 'priority', headerName: 'Priority', width: 100 },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 150,
      valueGetter: (params) => new Date(params.value).toLocaleDateString()
    },
    {
      field: 'actions',
      headerName: '',
      width: 60,
      sortable: false,
      renderCell: (params) => (
        <Tooltip title="Edit">
          <IconButton size="small" onClick={() => handleDayEdit(params.row)}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ]

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Box>
          <Typography variant="h5">Interventions Management</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage intervention content and day-specific configurations.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Intervention
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ height: 300, width: '100%' }}>
        <DataGrid
          rows={interventions}
          columns={allColumns}
          pageSize={10}
          rowsPerPageOptions={[10, 25, 50]}
          disableSelectionOnClick
          loading={loading}
          getRowHeight={() => 'auto'}
          onRowClick={(params) => handleRowSelection(params.row)}
          sx={{
            '& .MuiDataGrid-row:hover': {
              backgroundColor: '#f5f5f5',
              cursor: 'pointer'
            }
          }}
        />
      </Paper>

      {/* Intervention Days Grid */}
      {selectedIntervention && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" gutterBottom fontWeight={500}>
            Intervention Days for: {selectedIntervention.content.substring(0, 50)}...
          </Typography>
          <Paper sx={{ height: 400, width: '100%' }}>
            <DataGrid
              rows={selectedInterventionDays}
              columns={daysColumns}
              pageSize={10}
              rowsPerPageOptions={[10, 25, 50]}
              disableSelectionOnClick
              loading={loadingDays}
              getRowHeight={() => 'auto'}
              initialState={{
                sorting: {
                  sortModel: [{ field: 'day', sort: 'asc' }],
                },
              }}
            />
          </Paper>
        </Box>
      )}

      {/* Edit/Create Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedIntervention ? 'Edit Intervention' : 'Add New Intervention'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Content"
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              margin="normal"
              required
              multiline
              rows={10}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {selectedIntervention ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewDialog} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
        <DialogTitle>View Intervention</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="h6" gutterBottom>Content:</Typography>
            <Paper sx={{ p: 2, mb: 2, backgroundColor: '#f5f5f5' }}>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {selectedIntervention?.content}
              </Typography>
            </Paper>

            <Typography variant="h6" gutterBottom>Linked Diagnosis Types:</Typography>
            <Box sx={{ mb: 2 }}>
              {selectedIntervention?.links
                .filter(link => link.diagnosis_code)
                .map((link, index) => (
                  <Chip
                    key={index}
                    label={`${link.diagnosis_code} Stage ${link.diagnosis_stage}: ${link.diagnosis_description}`}
                    color="primary"
                    variant="outlined"
                    sx={{ mr: 1, mb: 1 }}
                  />
                ))}
            </Box>

            <Typography variant="h6" gutterBottom>Linked Treatment Types:</Typography>
            <Box>
              {selectedIntervention?.links
                .filter(link => link.treatment_code)
                .map((link, index) => (
                  <Chip
                    key={index}
                    label={`${link.treatment_code}: ${link.treatment_description}`}
                    color="secondary"
                    variant="outlined"
                    sx={{ mr: 1, mb: 1 }}
                  />
                ))}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Day Edit Dialog */}
      <Dialog open={dayEditDialog} onClose={handleDayEditClose} maxWidth="md" fullWidth>
        <DialogTitle>
          Edit Day {selectedDay?.day} Content
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Content"
              value={dayFormData.content}
              onChange={(e) => setDayFormData({ ...dayFormData, content: e.target.value })}
              margin="normal"
              required
              multiline
              rows={6}
            />
            <TextField
              fullWidth
              label="Priority"
              type="number"
              value={dayFormData.priority}
              onChange={(e) => setDayFormData({ ...dayFormData, priority: parseInt(e.target.value) || 1 })}
              margin="normal"
              inputProps={{ min: 1 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDayEditClose}>Cancel</Button>
          <Button onClick={handleDayEditSubmit} variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default Interventions 